// @ts-nocheck
console.log("✅ FILE LOADED: RegistrationFormsPage.tsx");

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Plus, Trash2, FileText, Edit, Loader2, User, Mail, Phone, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { getSessionUser } from '@/lib/authSession';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'dropdown';
  required: boolean;
  options?: string[];
  is_coach_name_field?: boolean;
  is_coach_email_field?: boolean;
  is_coach_phone_field?: boolean;
}

interface RegistrationForm {
  id: string;
  name: string;
  description: string;
  fields: FormField[];
  event_type_id?: string;
  event_category_id?: string;
  created_at: string;
  created_by: string;
}

interface EventType {
  id: string;
  name: string;
}

interface EventCategory {
  id: string;
  name: string;
}

// Editable Field Card Component
interface EditableFieldCardProps {
  field: FormField;
  onSave: (fieldId: string, updates: Partial<FormField>) => void;
  onCancel: () => void;
  existingFields: FormField[];
}

const EditableFieldCard: React.FC<EditableFieldCardProps> = ({ field, onSave, onCancel, existingFields }) => {
  const [editedField, setEditedField] = useState<FormField>({ ...field });
  const [options, setOptions] = useState(field.options?.join(', ') || '');

  const handleSave = () => {
    const updates: Partial<FormField> = {
      label: editedField.label,
      type: editedField.type,
      required: editedField.required,
      is_coach_name_field: editedField.is_coach_name_field,
      is_coach_email_field: editedField.is_coach_email_field,
      is_coach_phone_field: editedField.is_coach_phone_field,
      options: editedField.type === 'dropdown' ? options.split(',').map(opt => opt.trim()).filter(Boolean) : undefined
    };
    onSave(field.id, updates);
  };

  return (
    <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Field Label</Label>
          <Input
            value={editedField.label}
            onChange={(e) => setEditedField(prev => ({ ...prev, label: e.target.value }))}
            placeholder="Field label"
          />
        </div>
        <div className="space-y-2">
          <Label>Field Type</Label>
          <Select 
            value={editedField.type} 
            onValueChange={(value) => setEditedField(prev => ({ 
              ...prev, 
              type: value as FormField['type'],
              // Reset coach email if changing away from email type
              is_coach_email_field: value !== 'email' ? false : prev.is_coach_email_field,
              // Reset coach phone if changing away from phone type  
              is_coach_phone_field: value !== 'phone' ? false : prev.is_coach_phone_field
            }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="dropdown">Dropdown</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {editedField.type === 'dropdown' && (
        <div className="space-y-2">
          <Label>Dropdown Options (comma-separated)</Label>
          <Input
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            placeholder="Option 1, Option 2, Option 3"
          />
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            checked={editedField.required}
            onCheckedChange={(checked) => setEditedField(prev => ({ ...prev, required: checked === true }))}
          />
          <Label>Required field</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            checked={editedField.is_coach_name_field || false}
            onCheckedChange={(checked) => setEditedField(prev => ({ ...prev, is_coach_name_field: checked === true }))}
            disabled={existingFields.some(f => f.id !== field.id && f.is_coach_name_field)}
          />
          <Label className="flex items-center space-x-1">
            <User className="h-3 w-3" />
            <span>Coach Name Field</span>
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            checked={editedField.is_coach_email_field || false}
            onCheckedChange={(checked) => setEditedField(prev => ({ ...prev, is_coach_email_field: checked === true }))}
            disabled={existingFields.some(f => f.id !== field.id && f.is_coach_email_field) || editedField.type !== 'email'}
          />
          <Label className="flex items-center space-x-1">
            <Mail className="h-3 w-3" />
            <span>Coach Email Field</span>
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            checked={editedField.is_coach_phone_field || false}
            onCheckedChange={(checked) => setEditedField(prev => ({ ...prev, is_coach_phone_field: checked === true }))}
            disabled={existingFields.some(f => f.id !== field.id && f.is_coach_phone_field) || editedField.type !== 'phone'}
          />
          <Label className="flex items-center space-x-1">
            <Phone className="h-3 w-3" />
            <span>Coach Phone Field</span>
          </Label>
        </div>

        {editedField.is_coach_phone_field && editedField.type !== 'phone' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Coach phone field must be of type "Phone"
            </AlertDescription>
          </Alert>
        )}

        {editedField.is_coach_email_field && editedField.type !== 'email' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Coach email field must be of type "Email"
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!editedField.label.trim()}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const RegistrationFormsPage: React.FC = () => {
  console.log("✅ COMPONENT MOUNTED: RegistrationFormsPage");
  console.log("✅ RegistrationFormsPage mounted");
  console.log("🚀 RegistrationFormsPage component mounted");
  
  // Create form state
  const [forms, setForms] = useState<RegistrationForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState<'text' | 'email' | 'phone' | 'number' | 'dropdown'>('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldOptions, setNewFieldOptions] = useState("");
  const [newFieldIsCoachName, setNewFieldIsCoachName] = useState(false);
  const [newFieldIsCoachEmail, setNewFieldIsCoachEmail] = useState(false);
  const [newFieldIsCoachPhone, setNewFieldIsCoachPhone] = useState(false);

  // New state for event types and categories
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [eventCategories, setEventCategories] = useState<EventCategory[]>([]);
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string>("");
  const [selectedEventCategoryId, setSelectedEventCategoryId] = useState<string>("");
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [dropdownError, setDropdownError] = useState<string | null>(null);

  useEffect(() => {
    fetchForms();
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      setLoadingDropdowns(true);
      setDropdownError(null);

      // Fetch event types
      const { data: eventTypesData, error: eventTypesError } = await supabase
        .from('event_types')
        .select('id, name')
        .order('name');

      if (eventTypesError) throw eventTypesError;

      // Fetch event categories
      const { data: eventCategoriesData, error: eventCategoriesError } = await supabase
        .from('event_categories')
        .select('id, name')
        .order('name');

      if (eventCategoriesError) throw eventCategoriesError;

      setEventTypes(eventTypesData || []);
      setEventCategories(eventCategoriesData || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      setDropdownError('Failed to load event types and categories');
      toast.error("Failed to load event types and categories");
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const fetchForms = async () => {
    console.log("🔍 Loading forms... starting Supabase query");
    try {
      const { data, error } = await supabase
        .from('event_registration_forms')
        .select(`
          *,
          profiles:created_by (name)
        `)
        .order('created_at', { ascending: false });

      console.log("📡 Supabase query completed");
      console.log("✅ Forms fetched - data:", data);
      console.log("❌ Supabase error (if any):", error);

      if (error) throw error;
      
      console.log("📊 Setting forms state with data:", data || []);
      setForms(data || []);
      
      if (!data || data.length === 0) {
        console.log("⚠️ No forms found in database");
      } else {
        console.log(`✨ Successfully loaded ${data.length} forms`);
      }
    } catch (error) {
      console.error('❌ Error fetching registration forms:', error);
      toast.error("Failed to load registration forms");
    } finally {
      console.log("🏁 fetchForms completed, setting loading to false");
      setLoading(false);
    }
  };

  const handleAddField = () => {
    console.log("🔧 Adding new field:", { newFieldLabel, newFieldType, newFieldRequired, newFieldIsCoachName, newFieldIsCoachEmail, newFieldIsCoachPhone });
    
    if (!newFieldLabel.trim()) {
      console.log("⚠️ Field label is empty, showing error");
      toast.error("Field label is required");
      return;
    }

    // Validation for coach fields
    if (newFieldIsCoachName && formFields.some(field => field.is_coach_name_field)) {
      toast.error("Only one field can be designated as the coach name field");
      return;
    }

    if (newFieldIsCoachEmail && formFields.some(field => field.is_coach_email_field)) {
      toast.error("Only one field can be designated as the coach email field");
      return;
    }

    if (newFieldIsCoachPhone && formFields.some(field => field.is_coach_phone_field)) {
      toast.error("Only one field can be designated as the coach phone field");
      return;
    }

    if (newFieldIsCoachEmail && newFieldType !== 'email') {
      toast.error("Coach email field must be of type 'email'");
      return;
    }

    if (newFieldIsCoachPhone && newFieldType !== 'phone') {
      toast.error("Coach phone field must be of type 'phone'");
      return;
    }

    const newField: FormField = {
      id: Date.now().toString(),
      label: newFieldLabel,
      type: newFieldType,
      required: newFieldRequired,
      options: newFieldType === 'dropdown' ? newFieldOptions.split(',').map(opt => opt.trim()).filter(Boolean) : undefined,
      is_coach_name_field: newFieldIsCoachName,
      is_coach_email_field: newFieldIsCoachEmail,
      is_coach_phone_field: newFieldIsCoachPhone
    };

    console.log("✅ Created new field object:", newField);
    setFormFields(prev => [...prev, newField]);
    setNewFieldLabel("");
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldOptions("");
    setNewFieldIsCoachName(false);
    setNewFieldIsCoachEmail(false);
    setNewFieldIsCoachPhone(false);
    console.log("🧹 Reset field form inputs");
  };

  const handleRemoveField = (fieldId: string) => {
    console.log("🗑️ Removing field with ID:", fieldId);
    setFormFields(prev => prev.filter(field => field.id !== fieldId));
    setEditingFieldId(null);
  };

  const handleEditField = (fieldId: string) => {
    setEditingFieldId(fieldId);
  };

  const handleUpdateField = (fieldId: string, updates: Partial<FormField>) => {
    console.log("✏️ Updating field:", fieldId, updates);
    
    // Validation for coach fields
    if (updates.is_coach_name_field && formFields.some(field => field.id !== fieldId && field.is_coach_name_field)) {
      toast.error("Only one field can be designated as the coach name field");
      return;
    }

    if (updates.is_coach_email_field && formFields.some(field => field.id !== fieldId && field.is_coach_email_field)) {
      toast.error("Only one field can be designated as the coach email field");
      return;
    }

    if (updates.is_coach_phone_field && formFields.some(field => field.id !== fieldId && field.is_coach_phone_field)) {
      toast.error("Only one field can be designated as the coach phone field");
      return;
    }

    if (updates.is_coach_email_field && updates.type !== 'email') {
      const field = formFields.find(f => f.id === fieldId);
      if (field && field.type !== 'email') {
        toast.error("Coach email field must be of type 'email'");
        return;
      }
    }

    if (updates.is_coach_phone_field && updates.type !== 'phone') {
      const field = formFields.find(f => f.id === fieldId);
      if (field && field.type !== 'phone') {
        toast.error("Coach phone field must be of type 'phone'");
        return;
      }
    }

    setFormFields(prev => prev.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
    setEditingFieldId(null);
  };

  const handleCancelEdit = () => {
    setEditingFieldId(null);
  };

  const handleCreateForm = async () => {
    console.log("💾 Creating new form with data:", { formName, formDescription, formFields });
    
    if (!formName.trim()) {
      console.log("⚠️ Form name is empty, showing error");
      toast.error("Form name is required");
      return;
    }

    if (formFields.length === 0) {
      console.log("⚠️ No fields added, showing error");
      toast.error("At least one field is required");
      return;
    }

    if (!selectedEventTypeId) {
      console.log("⚠️ Event type is required, showing error");
      toast.error("Event type is required");
      return;
    }

    if (!selectedEventCategoryId) {
      console.log("⚠️ Event category is required, showing error");
      toast.error("Event category is required");
      return;
    }

    try {
      console.log("🔐 Getting current user for form creation");
      const { data: { user } } = await getSessionUser();
      if (!user) {
        console.log("❌ User not authenticated");
        throw new Error("User not authenticated");
      }
      console.log("✅ User authenticated:", user.id);

      console.log("📤 Inserting form into database");
      const { error } = await supabase
        .from('event_registration_forms')
        .insert({
          name: formName,
          description: formDescription,
          fields: formFields,
          event_type_id: selectedEventTypeId,
          event_category_id: selectedEventCategoryId,
          created_by: user.id
        });

      console.log("📡 Form insert query completed, error:", error);

      if (error) throw error;

      console.log("🎉 Registration form created successfully!");
      toast.success("Registration form created successfully!");
      setIsCreateModalOpen(false);
      setFormName("");
      setFormDescription("");
      setFormFields([]);
      setSelectedEventTypeId("");
      setSelectedEventCategoryId("");
      console.log("🧹 Reset form creation state");
      fetchForms();
    } catch (error) {
      console.error('❌ Error creating registration form:', error);
      toast.error("Failed to create registration form");
    }
  };

  const handleDeleteForm = async (formId: string) => {
    console.log("🗑️ Attempting to delete form with ID:", formId);
    
    if (!confirm("Are you sure you want to delete this registration form?")) {
      console.log("❌ User cancelled form deletion");
      return;
    }

    try {
      console.log("📤 Deleting form from database");
      const { error } = await supabase
        .from('event_registration_forms')
        .delete()
        .eq('id', formId);

      console.log("📡 Form delete query completed, error:", error);

      if (error) throw error;

      console.log("🎉 Registration form deleted successfully!");
      toast.success("Registration form deleted successfully!");
      fetchForms();
    } catch (error) {
      console.error('❌ Error deleting registration form:', error);
      toast.error("Failed to delete registration form");
    }
  };

  const handleEditForm = (form: RegistrationForm) => {
    console.log("✏️ Opening form for editing:", form);
    setIsEditMode(true);
    setEditingFormId(form.id);
    setFormName(form.name);
    setFormDescription(form.description || "");
    setFormFields(form.fields || []);
    setSelectedEventTypeId(form.event_type_id || "");
    setSelectedEventCategoryId(form.event_category_id || "");
    setIsCreateModalOpen(true);
  };

  const handleUpdateForm = async () => {
    console.log("💾 Updating form with data:", { editingFormId, formName, formDescription, formFields });
    
    if (!formName.trim()) {
      console.log("⚠️ Form name is empty, showing error");
      toast.error("Form name is required");
      return;
    }

    if (formFields.length === 0) {
      console.log("⚠️ No fields added, showing error");
      toast.error("At least one field is required");
      return;
    }

    if (!selectedEventTypeId) {
      console.log("⚠️ Event type is required, showing error");
      toast.error("Event type is required");
      return;
    }

    if (!selectedEventCategoryId) {
      console.log("⚠️ Event category is required, showing error");
      toast.error("Event category is required");
      return;
    }

    try {
      console.log("📤 Updating form in database");
      const { error } = await supabase
        .from('event_registration_forms')
        .update({
          name: formName,
          description: formDescription,
          fields: formFields,
          event_type_id: selectedEventTypeId,
          event_category_id: selectedEventCategoryId,
        })
        .eq('id', editingFormId);

      console.log("📡 Form update query completed, error:", error);

      if (error) throw error;

      console.log("🎉 Registration form updated successfully!");
      toast.success("Registration form updated successfully!");
      resetFormState();
      fetchForms();
    } catch (error) {
      console.error('❌ Error updating registration form:', error);
      toast.error("Failed to update registration form");
    }
  };

  const resetFormState = () => {
    setIsCreateModalOpen(false);
    setIsEditMode(false);
    setEditingFormId(null);
    setFormName("");
    setFormDescription("");
    setFormFields([]);
    setSelectedEventTypeId("");
    setSelectedEventCategoryId("");
    setNewFieldLabel("");
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldOptions("");
    setNewFieldIsCoachName(false);
    setNewFieldIsCoachEmail(false);
    setNewFieldIsCoachPhone(false);
    console.log("🧹 Reset all form state");
  };

  if (loading) {
    console.log("⏰ Component in loading state");
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading registration forms...</div>
      </div>
    );
  }

  console.log("🖼️ Rendering main component with", forms.length, "forms");

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Registration Forms</h1>
          <p className="text-gray-600 mt-2">Manage reusable registration forms for events</p>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create New Form
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isEditMode ? 'Edit Registration Form' : 'Create Registration Form'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Form Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="form-name">Form Name</Label>
                  <Input
                    id="form-name"
                    placeholder="e.g., Baseball Showcase Registration"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="form-description">Description</Label>
                  <Textarea
                    id="form-description"
                    placeholder="Brief description of what this form is used for"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                  />
                </div>

                {/* Event Type and Category Dropdowns */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event-type">Event Type <span className="text-red-500">*</span></Label>
                    {loadingDropdowns ? (
                      <div className="flex items-center space-x-2 h-10 px-3 py-2 border rounded-md bg-gray-50">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-gray-500">Loading...</span>
                      </div>
                    ) : dropdownError ? (
                      <div className="h-10 px-3 py-2 border rounded-md bg-red-50 border-red-200 text-red-600 text-sm flex items-center">
                        Error loading types
                      </div>
                    ) : (
                      <Select value={selectedEventTypeId} onValueChange={setSelectedEventTypeId}>
                        <SelectTrigger id="event-type">
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="event-category">Event Category <span className="text-red-500">*</span></Label>
                    {loadingDropdowns ? (
                      <div className="flex items-center space-x-2 h-10 px-3 py-2 border rounded-md bg-gray-50">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-gray-500">Loading...</span>
                      </div>
                    ) : dropdownError ? (
                      <div className="h-10 px-3 py-2 border rounded-md bg-red-50 border-red-200 text-red-600 text-sm flex items-center">
                        Error loading categories
                      </div>
                    ) : (
                      <Select value={selectedEventCategoryId} onValueChange={setSelectedEventCategoryId}>
                        <SelectTrigger id="event-category">
                          <SelectValue placeholder="Select event category" />
                        </SelectTrigger>
                        <SelectContent>
                          {eventCategories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Form Fields</h3>
                
                {/* Add New Field */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Add New Field</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Field Label</Label>
                        <Input
                          placeholder="e.g., Player Name"
                          value={newFieldLabel}
                          onChange={(e) => setNewFieldLabel(e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Field Type</Label>
                        <Select value={newFieldType} onValueChange={(value) => {
                          setNewFieldType(value as any);
                          // Reset coach flags when changing field type
                          if (value !== 'email') setNewFieldIsCoachEmail(false);
                          if (value !== 'phone') setNewFieldIsCoachPhone(false);
                        }}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="phone">Phone</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                            <SelectItem value="dropdown">Dropdown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {newFieldType === 'dropdown' && (
                      <div className="space-y-2">
                        <Label>Dropdown Options (comma-separated)</Label>
                        <Input
                          placeholder="Option 1, Option 2, Option 3"
                          value={newFieldOptions}
                          onChange={(e) => setNewFieldOptions(e.target.value)}
                        />
                      </div>
                    )}
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="field-required"
                          checked={newFieldRequired}
                          onCheckedChange={(checked) => setNewFieldRequired(checked === true)}
                        />
                        <Label htmlFor="field-required">Required field</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="coach-name-field"
                          checked={newFieldIsCoachName}
                          onCheckedChange={(checked) => setNewFieldIsCoachName(checked === true)}
                          disabled={formFields.some(field => field.is_coach_name_field)}
                        />
                        <Label htmlFor="coach-name-field" className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>Use this field as the coach name for invitations</span>
                        </Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="coach-email-field"
                          checked={newFieldIsCoachEmail}
                          onCheckedChange={(checked) => setNewFieldIsCoachEmail(checked === true)}
                          disabled={formFields.some(field => field.is_coach_email_field) || newFieldType !== 'email'}
                        />
                        <Label htmlFor="coach-email-field" className="flex items-center space-x-1">
                          <Mail className="h-3 w-3" />
                          <span>Use this field as the coach email for invitations</span>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="coach-phone-field"
                          checked={newFieldIsCoachPhone}
                          onCheckedChange={(checked) => setNewFieldIsCoachPhone(checked === true)}
                          disabled={formFields.some(field => field.is_coach_phone_field) || newFieldType !== 'phone'}
                        />
                        <Label htmlFor="coach-phone-field" className="flex items-center space-x-1">
                          <Phone className="h-3 w-3" />
                          <span>Use this field as the coach phone for invitations</span>
                        </Label>
                      </div>
                      
                      {newFieldIsCoachEmail && newFieldType !== 'email' && (
                        <Alert>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            Coach email field should be of type "Email" for proper validation.
                          </AlertDescription>
                        </Alert>
                      )}

                      {newFieldIsCoachPhone && newFieldType !== 'phone' && (
                        <Alert>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            Coach phone field should be of type "Phone" for proper validation and formatting.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                    
                    <Button onClick={handleAddField} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Field
                    </Button>
                  </CardContent>
                </Card>

                {/* Current Fields */}
                {formFields.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Form Preview</CardTitle>
                    </CardHeader>
                     <CardContent className="space-y-3">
                       {formFields.map((field) => (
                         <div key={field.id} className="p-3 border rounded-lg">
                           {editingFieldId === field.id ? (
                             /* Editing Mode */
                             <EditableFieldCard 
                               field={field}
                               onSave={handleUpdateField}
                               onCancel={handleCancelEdit}
                               existingFields={formFields}
                             />
                           ) : (
                             /* Display Mode */
                             <div className="flex items-center justify-between">
                               <div className="flex-1 cursor-pointer" onClick={() => handleEditField(field.id)}>
                                 <div className="flex items-center space-x-2">
                                   <span className="font-medium hover:text-blue-600">{field.label}</span>
                                   {field.is_coach_name_field && (
                                     <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                       <User className="h-3 w-3 mr-1" />
                                       Coach Name
                                     </Badge>
                                    )}
                                    {field.is_coach_email_field && (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                        <Mail className="h-3 w-3 mr-1" />
                                        Coach Email
                                      </Badge>
                                    )}
                                    {field.is_coach_phone_field && (
                                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                        <Phone className="h-3 w-3 mr-1" />
                                        Coach Phone
                                      </Badge>
                                    )}
                                 </div>
                                 <div className="text-sm text-gray-500 hover:text-gray-700">
                                   Type: {field.type} • {field.required ? 'Required' : 'Optional'}
                                   {field.options && (
                                     <span> • Options: {field.options.join(', ')}</span>
                                   )}
                                 </div>
                               </div>
                               <div className="flex items-center space-x-2">
                                 <Button
                                   variant="ghost"
                                   size="sm"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleEditField(field.id);
                                   }}
                                 >
                                   <Edit className="h-4 w-4" />
                                 </Button>
                                 <Button
                                   variant="ghost"
                                   size="sm"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     handleRemoveField(field.id);
                                   }}
                                 >
                                   <Trash2 className="h-4 w-4" />
                                 </Button>
                               </div>
                             </div>
                           )}
                         </div>
                       ))}
                     </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={resetFormState}>
                  Cancel
                </Button>
                <Button onClick={isEditMode ? handleUpdateForm : handleCreateForm}>
                  {isEditMode ? 'Update Form' : 'Create Form'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Forms Table */}
      <Card>
        <CardHeader>
          <CardTitle>Registration Forms</CardTitle>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No registration forms yet</h3>
              <p className="text-gray-600 mb-4">Create your first registration form to get started.</p>
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Form
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Fields</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form.id}>
                    <TableCell className="font-medium">{form.name}</TableCell>
                    <TableCell>{form.description || "No description"}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {form.fields?.length || 0} fields
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(form.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditForm(form)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteForm(form.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationFormsPage;
